import { Info } from 'lucide-react';

import type { AdminRunTelemetry } from '@/api/adminClient';
import {
  MARKER_LABELS,
  SEGMENT_META,
  formatDurationMs,
  waitVerdict,
} from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

/** One-line version of the breakdown for tables. */
export function WaitBar({ telemetry, className }: { telemetry: AdminRunTelemetry; className?: string }) {
  const breakdown = telemetry.waitBreakdown;
  if (!breakdown || breakdown.totalMs <= 0) return null;
  return (
    <div
      className={cn('flex h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
      role="img"
      aria-label={breakdown.segments
        .map((segment) => `${SEGMENT_META[segment.key].label} ${formatDurationMs(segment.durationMs)}`)
        .join(', ')}
    >
      {breakdown.segments.map((segment, index) => {
        const width = (segment.durationMs / breakdown.totalMs) * 100;
        if (width <= 0) return null;
        return (
          <div
            key={`${segment.key}:${index}`}
            className={cn('h-full', SEGMENT_META[segment.key].swatch)}
            style={{ width: `${width}%` }}
            title={`${SEGMENT_META[segment.key].label}: ${formatDurationMs(segment.durationMs)}`}
          />
        );
      })}
    </div>
  );
}

export function WaitBreakdown({ telemetry }: { telemetry: AdminRunTelemetry }) {
  const breakdown = telemetry.waitBreakdown;
  if (!breakdown || breakdown.totalMs <= 0) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          No phase timing was recorded for this run yet. Queued runs, plan-only runs and runs from
          before phase telemetry have nothing to break down.
        </p>
      </div>
    );
  }

  const verdict = waitVerdict(telemetry);
  const total = breakdown.totalMs;

  return (
    <div className="space-y-5">
      {verdict ? (
        <p className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm leading-6 text-foreground">
          {verdict.sentence}
        </p>
      ) : null}

      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
          <span>{MARKER_LABELS[breakdown.fromKey]}</span>
          <span className="font-medium tabular-nums text-foreground">{formatDurationMs(total)}</span>
          <span>{MARKER_LABELS[breakdown.toKey]}</span>
        </div>
        <div
          className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={`Wait breakdown totalling ${formatDurationMs(total)}`}
        >
          {breakdown.segments.map((segment, index) => {
            const width = (segment.durationMs / total) * 100;
            if (width <= 0) return null;
            return (
              <div
                key={`${segment.key}:${index}`}
                className={cn(
                  'h-full border-r border-background/60 last:border-r-0',
                  SEGMENT_META[segment.key].swatch,
                  verdict?.segment === segment.key ? 'opacity-100' : 'opacity-70',
                )}
                style={{ width: `${Math.max(width, 0.6)}%` }}
                title={`${SEGMENT_META[segment.key].label}: ${formatDurationMs(segment.durationMs)}`}
              />
            );
          })}
        </div>
      </div>

      <ul className="divide-y divide-border rounded-lg border border-border">
        {breakdown.segments.map((segment, index) => {
          const meta = SEGMENT_META[segment.key];
          const share = Math.round((segment.durationMs / total) * 100);
          const dominant = verdict?.segment === segment.key;
          return (
            <li
              key={`${segment.key}:${index}`}
              className={cn('grid grid-cols-[auto_1fr_auto] items-start gap-3 px-4 py-3', dominant && 'bg-muted/30')}
            >
              <span className={cn('mt-1.5 h-2.5 w-2.5 rounded-sm', meta.swatch)} aria-hidden="true" />
              <div className="min-w-0">
                <p className={cn('text-sm', dominant ? 'font-semibold text-foreground' : 'text-foreground')}>
                  {meta.label}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                  {MARKER_LABELS[segment.fromKey]} → {MARKER_LABELS[segment.toKey]}. {meta.hint}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold tabular-nums">{formatDurationMs(segment.durationMs)}</p>
                <p className="text-xs tabular-nums text-muted-foreground">{share}%</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
